'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Input, Skeleton, useKiaoConfirm } from '@kiao-voice/ui-kit'
import { getJoinLink, rotateJoinLink } from './join-link-api-client'

/**
 * `02 新增成員` 的「方式二：加入連結」。標題與說明由呼叫端（`AddMemberSection`）
 * 出，這裡只負責連結本身的載入、複製與重新產生。
 */
export function JoinLinkPanel({ organizationId }: { organizationId: number }) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  // 'load' ＝ 一開始就讀不到連結；'action' ＝ 複製或重新產生失敗（畫面上有連結可手動選取）。
  const [error, setError] = useState<'load' | 'action' | null>(null)
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    let disposed = false
    setLoading(true)
    setError(null)
    setToken(null)
    getJoinLink(organizationId)
      .then((result) => {
        if (!disposed) setToken(result.token)
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, { source: 'features/team/join-link-panel.tsx' })
        if (!disposed) setError('load')
      })
      .finally(() => {
        if (!disposed) setLoading(false)
      })
    return () => {
      disposed = true
    }
  }, [organizationId])
  const url = token ? `${window.location.origin}/invite/join/${token}` : ''
  const rotate = async () => {
    if (busy) return
    if (
      token &&
      !(await confirm({
        title: t('team.joinLinkRotateTitle'),
        message: t('team.joinLinkRotateBody'),
        confirmText: t('team.joinLinkRotate'),
        cancelText: t('common:cancel'),
      }))
    )
      return
    setBusy(true)
    setError(null)
    setCopied(false)
    try {
      setToken((await rotateJoinLink(organizationId)).token)
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/team/join-link-panel.tsx' })
      setError('action')
    } finally {
      setBusy(false)
    }
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setError(null)
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/team/join-link-panel.tsx' })
      setError('action')
    }
  }
  return (
    <div data-testid="join-link-panel">
      {loading ? (
        <div role="status" aria-label={t('team.processing')} className="mt-[var(--gap-sm)]">
          <Skeleton width="min(520px, 100%)" height={38} />
        </div>
      ) : (
        <div className="mt-[var(--gap-sm)] flex flex-wrap items-center gap-[var(--gap-sm)]">
          {token && (
            <>
              <Input
                aria-label={t('team.wayLinkTitle')}
                value={url}
                readOnly
                onFocus={(event) => event.target.select()}
                className="w-full min-w-0 font-mono text-sm text-muted sm:w-auto sm:flex-1"
              />
              <Button
                size="control"
                variant="secondary"
                onClick={() => void copy()}
                disabled={busy}
              >
                {t(copied ? 'team.joinLinkCopied' : 'team.joinLinkCopy')}
              </Button>
            </>
          )}
          <Button
            size="control"
            variant={token ? 'text' : 'secondary'}
            disabled={busy}
            onClick={() => void rotate()}
          >
            {t(token ? 'team.joinLinkRotate' : 'team.joinLinkCreate')}
          </Button>
        </div>
      )}
      {error ? (
        <Alert className="mt-[var(--gap-sm)]">
          {t(error === 'load' ? 'team.joinLinkLoadFailed' : 'team.joinLinkError')}
        </Alert>
      ) : null}
    </div>
  )
}
